<template>
  <div 
    class="product-block" 
    :class="{ 'is-locked': !isActive && !hasActiveAt }"
    @click="handleDetailsClick" 
    @mouseenter="isHovered = true" 
    @mouseleave="isHovered = false"
  >
    <!-- Image Section -->
    <div class="image-area">
      <div class="badge-container">
        <span v-if="!isActive && hasActiveAt" class="tag-badge next-drop">{{ t('product.tags.next_drop') }}</span>
        <span v-else-if="!isActive" class="tag-badge next-drop">{{ t('product.tags.coming_soon') }}</span>
        <template v-else>
          <span v-if="isLimited" class="tag-badge limited">{{ t('product.tags.limited') }}</span>
          <span v-if="isNew" class="tag-badge new">{{ t('product.tags.new') }}</span>
        </template>
      </div>
      
      <!-- Anteprima nitida con logo pulsante per creare hype sul prossimo drop -->
      <div v-if="!isActive && !hasActiveAt && teaserImage" class="product-image-placeholder teaser-blur" :style="teaserBackgroundStyle">
        <img :src="teaserImage" :alt="product.name" class="teaser-image" @error="handleImageError">
        <div class="teaser-overlay">
          <div class="teaser-logo-badge">
            <img :src="logo" alt="Billy B" class="teaser-logo-icon">
          </div>
        </div>
      </div>

      <div v-else-if="!isActive && !hasActiveAt" class="product-image-placeholder" :style="teaserBackgroundStyle">
        <div class="teaser-logo-badge">
          <img :src="logo" alt="Billy B" class="teaser-logo-icon">
        </div>
      </div>

      <div v-else-if="!isActive && hasActiveAt" class="main-image-wrapper">
        <img 
          :src="displayImage" 
          :alt="product.name"
          class="block-image"
          @error="handleImageError"
        />
      </div>

      <div v-else class="main-image-wrapper">
        <img 
          :src="displayImage" 
          :alt="product.name"
          class="block-image"
          @error="handleImageError"
        />
      </div>

      <div v-if="!product.inStock && isActive" class="sold-out-banner">
        <span>{{ t('product.status.sold_out') }}</span>
      </div>
    </div>

    <!-- Info Section -->
    <div class="info-area">
      <div class="brand-label">{{ t('brand.name') }}</div>
      <div v-if="categoryName" class="category-label">{{ categoryName }}</div>
      <div v-if="product.categoryTypeSku" class="sku-row">
        <span class="sku-label-text">COD.</span>
        <span class="sku-value">{{ product.categoryTypeSku }}</span>
      </div>
      <h3 class="item-name">{{ product.name?.toUpperCase() }}</h3>
      
      <div v-if="isActive" class="product-price-block">
        <div class="price-values-container">
          <template v-if="product.discount > 0">
            <span class="price-old">€ {{ (displayOldPrice).toFixed(2).replace('.', ',') }}</span>
            <span class="price-divider"></span>
            <span class="price-final">€ {{ (product.price || 0).toFixed(2).replace('.', ',') }}</span>
            <span class="price-badge-discount">-{{ Math.round(product.discount) }}%</span>
          </template>

          <template v-else>
            <span class="price-main-regular">€ {{ (product.price || 0).toFixed(2).replace('.', ',') }}</span>
          </template>
        </div>
        <div class="price-vat-label">{{ t('product.labels.vat_included') }}</div>
      </div>

      <div v-else class="coming-soon-spacer"></div>
      
      <button 
        class="btn-premium-action" 
        :class="{ 'btn-disabled': !isActive, 'btn-countdown': hasCountdown }"
        :disabled="!isActive"
        @click.stop="handleDetailsClick"
      >
        <template v-if="isActive">
          <span>{{ t('product.btn_details') }}</span>
          <svg class="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </template>
        <div v-else-if="hasCountdown" class="countdown-wrapper">
          <span class="countdown-label">{{ t('product.release_in') }}</span>
          <div class="countdown-digits">
            <template v-for="(segment, index) in countdownSegments" :key="segment.unit">
              <div class="countdown-segment">
                <span class="countdown-value">{{ segment.value }}</span>
                <span class="countdown-unit">{{ segment.unit }}</span>
              </div>
              <span v-if="index < countdownSegments.length - 1" class="countdown-sep">:</span>
            </template>
          </div>
        </div>
        <span v-else>{{ t('product.coming_soon') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useProductImages } from '@/composables/useProductImages'
import { useLanguage } from '@/composables/useLanguage'
import logo from '@/assets/images/logo.png'

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['view-details'])
const { getImageUrl } = useProductImages()
const { t } = useLanguage()

const categoryName = computed(() => {
  const typeId = Number(props.product.categoryTypeId)
  // Mapping temporaneo come richiesto: 1, 2, 3 -> TSHIRT
  if ([1, 2, 3].includes(typeId)) {
    return 'TSHIRT'
  }
  return ''
})

const isHovered = ref(false)
const remainingMs = ref(0)
let countdownInterval = null

const allImages = computed(() => {
  const uniquePaths = new Set()
  
  const addImage = (img) => {
    const path = img?.imageUrl || img?.imageName || img?.url || img
    if (typeof path === 'string' && path) {
      const fullPath = path.startsWith('/') || path.startsWith('http') ? path : getImageUrl(path)
      if (fullPath) uniquePaths.add(fullPath)
    }
  }

  if (Array.isArray(props.product.images)) {
    props.product.images.forEach(addImage)
  }
  
  if (Array.isArray(props.product.variants)) {
    props.product.variants.forEach(v => {
      if (Array.isArray(v.images)) v.images.forEach(addImage)
    })
  }

  if (Array.isArray(props.product.colors)) {
    props.product.colors.forEach(c => {
      if (c.frontImageFileName) addImage(c.frontImageFileName)
      if (c.backImageFileName) addImage(c.backImageFileName)
    })
  }
  
  return Array.from(uniquePaths)
})

const resolveImagePath = (img) => {
  const path = img?.imageUrl || img?.imageName || img?.url
  if (typeof path !== 'string' || !path) return null
  return path.startsWith('/') || path.startsWith('http') ? path : getImageUrl(path)
}

const displayImage = computed(() => {
  const imgs = allImages.value
  if (imgs.length === 0) return '/images/products/placeholder.png'

  if (isHovered.value) {
    // Immagine da mostrare in hover: flag isSecondary, altrimenti seconda immagine disponibile
    const secondaryImg = props.product.images?.find(i => i.isSecondary)
    return resolveImagePath(secondaryImg) || imgs[1] || imgs[0]
  }

  // Immagine principale: flag isPrimary, altrimenti prima immagine disponibile
  const primaryImg = props.product.images?.find(i => i.isPrimary)
  return resolveImagePath(primaryImg) || imgs[0]
})

const teaserImage = computed(() => {
  // Anteprima per i prodotti non ancora attivi: solo se esiste davvero un'immagine isPrimary
  const primaryImg = props.product.images?.find(i => i.isPrimary)
  return resolveImagePath(primaryImg) || allImages.value[0] || null
})

const handleDetailsClick = () => {
  if (props.product.inStock) {
    emit('view-details', props.product)
  }
}

const isNew = computed(() => props.product.isNew ?? false)
const isLimited = computed(() => props.product.isLimited ?? false)
const isActive = computed(() => props.product.isActive !== false) // Default to true if not specified

const teaserBackgroundStyle = computed(() => {
  const hex = props.product.colors?.[0]?.colorHexCode
  return hex ? { backgroundColor: hex } : {}
})

const activeAtTimestamp = computed(() => {
  if (!props.product.activeAt) return NaN
  const timestamp = Date.parse(props.product.activeAt)
  return Number.isNaN(timestamp) ? NaN : timestamp
})

const hasActiveAt = computed(() => Number.isFinite(activeAtTimestamp.value))

const hasCountdown = computed(() => {
  return !isActive.value && Number.isFinite(activeAtTimestamp.value) && remainingMs.value > 0
})

const countdownSegments = computed(() => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs.value / 1000))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const segments = []
  if (days > 0) segments.push({ value: String(days).padStart(2, '0'), unit: t('product.countdown.days') })
  segments.push({ value: String(hours).padStart(2, '0'), unit: t('product.countdown.hours') })
  segments.push({ value: String(minutes).padStart(2, '0'), unit: t('product.countdown.minutes') })
  segments.push({ value: String(seconds).padStart(2, '0'), unit: t('product.countdown.seconds') })
  return segments
})

const updateCountdown = () => {
  if (isActive.value || !Number.isFinite(activeAtTimestamp.value)) {
    remainingMs.value = 0
    return
  }

  remainingMs.value = Math.max(0, activeAtTimestamp.value - Date.now())
}

const displayOldPrice = computed(() => {
  return props.product.oldPrice || props.product.price || 0
})

const handleImageError = (e) => {
  const placeholder = '/images/products/placeholder.png'
  // Prevent infinite loop if placeholder also fails
  if (e.target.src.endsWith(placeholder)) {
    return
  }
  e.target.src = placeholder
}

onMounted(() => {
  updateCountdown()
  countdownInterval = window.setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  if (countdownInterval) window.clearInterval(countdownInterval)
})
</script>

<style scoped>
.product-block {
  background: #FFFFFF;
  transition: border-color 0.2s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #eaeaea;
}

.product-block:hover {
  border-color: #111111;
}

.image-area {
  position: relative;
  background-color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  aspect-ratio: 1.2 / 1;
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  background-color: #F8F8F8;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.teaser-blur {
  position: relative;
  background-color: #FFFFFF; /* evita lo sfondo grigio del placeholder ai bordi del teaser */
}

.teaser-image {
  width: 100%;
  height: 90%;
  object-fit: contain;
  filter: blur(8px) saturate(0.6);
  opacity: 0.55;
  transition: filter 0.4s ease, opacity 0.4s ease;
}

.teaser-image:hover {
  filter: blur(6px) saturate(0.7);
  opacity: 0.7;
}

.teaser-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.teaser-logo-badge {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
  animation: teaser-logo-pulse 3s infinite ease-in-out;
}

.teaser-logo-icon {
  width: 68px;
  filter: brightness(0); /* forza il logo in nero indipendentemente dal colore sorgente */
}

@keyframes teaser-logo-pulse {
  0% { transform: scale(0.92); opacity: 0.75; }
  50% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(0.92); opacity: 0.75; }
}

.badge-container {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.tag-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  display: inline-block;
}

.tag-badge.limited {
  background: #000;
  color: #fff;
}

.tag-badge.next-drop {
  background: #000;
  color: #fff;
}

.tag-badge.new {
  background: #fff;
  color: #000;
  border: 1px solid #000;
}

.main-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.is-locked .block-image {
  opacity: 0.45;
  filter: grayscale(20%);
  transition: all 0.3s ease;
}

.block-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.info-area {
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  flex: 1;
  text-align: left;
}

.brand-label {
  font-size: 9px;
  color: #999;
  letter-spacing: 1.5px;
  margin-bottom: 4px;
  font-weight: 500;
  text-transform: uppercase;
}

.category-label {
  font-size: 11px;
  color: #000;
  letter-spacing: 1px;
  margin-bottom: 4px;
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
}

.sku-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.sku-label-text {
  font-size: 8px;
  color: #bbb;
  font-weight: 500;
  letter-spacing: 1px;
}

.sku-value {
  background: transparent;
  color: #888888;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0;
  margin: 4px 0;
}

.item-name {
  font-size: 18px;
  font-weight: 800;
  color: #000;
  margin: 0 0 10px 0;
  letter-spacing: 0.3px;
  font-family: 'Montserrat', sans-serif;
  line-height: 1.1;
  text-transform: uppercase;
}

.product-price-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 15px;
}

.price-values-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.price-old {
  font-size: 13px;
  text-decoration: line-through;
  color: #999;
  font-family: 'Montserrat', sans-serif;
  line-height: 1;
}

.price-divider {
  width: 1px;
  height: 12px;
  background-color: #e0e0e0;
  margin: 0 10px;
}

.price-final {
  font-size: 15px;
  font-weight: 700;
  color: #111;
  font-family: 'Montserrat', sans-serif;
  line-height: 1;
}

.price-badge-discount {
  color: #7D2121;
  font-weight: 700;
  font-size: 12px;
  margin-left: 8px;
  font-family: 'Montserrat', sans-serif;
  line-height: 1;
}

.price-main-regular {
  font-size: 15px;
  font-weight: 700;
  color: #111;
  font-family: 'Montserrat', sans-serif;
  line-height: 1;
}

.price-vat-label {
  display: block;
  font-size: 8px;
  color: #999;
  font-weight: 400;
  margin-top: 4px;
  letter-spacing: 0.5px;
  width: 100%;
}

.coming-soon-spacer {
  height: 15px; /* Sostituisce il testo DISPONIBILE A BREVE per bilanciare il layout */
  margin-bottom: 15px;
}

.btn-premium-action {
  width: 100%;
  background: #000;
  color: #fff;
  border: none;
  padding: 14px;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s;
  font-family: 'Montserrat', sans-serif;
  border-radius: 0;
}

.btn-premium-action.btn-disabled {
  background: #f5f5f5;
  color: #999;
  border: 1px solid #e0e0e0;
  cursor: not-allowed;
}

.btn-premium-action.btn-disabled.btn-countdown {
  background: #0a0a0a;
  color: #fff;
  border: 1px solid #0a0a0a;
  cursor: default;
  padding: 10px 14px;
}

.countdown-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.countdown-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.countdown-digits {
  display: flex;
  align-items: flex-start;
  gap: 5px;
}

.countdown-segment {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 24px;
}

.countdown-value {
  font-family: 'Courier New', monospace;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #fff;
  line-height: 1;
}

.countdown-unit {
  font-size: 7px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 3px;
}

.countdown-sep {
  font-family: 'Courier New', monospace;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.3);
  line-height: 1;
  align-self: flex-start;
}

.btn-premium-action:hover:not(.btn-disabled) {
  background: #222;
}

.btn-arrow {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.btn-premium-action:hover .btn-arrow {
  transform: translateX(6px);
}

.sold-out-banner {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.sold-out-banner span {
  font-size: 18px;
  font-weight: 900;
  color: #000;
  border: 2px solid #000;
  padding: 10px 20px;
  letter-spacing: 2px;
}
</style>
